import { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { IoArrowBackOutline, IoPrintOutline } from 'react-icons/io5';
import { api } from '../api';

type Mode = 'aggregated' | 'detailed';

// Печатный «лист сборки» для склада.
// Аггрегирует все awaiting_packaging отправления по offer_id, чтобы кладовщик
// мог собрать все нужные позиции одним проходом по складу.

export function PickingListPage() {
  const [mode, setMode] = useState<Mode>('aggregated');
  const postings = useQuery({
    queryKey: ['fbs-postings', 'awaiting_packaging', 'all'],
    queryFn: () => api.fbsPostings('awaiting_packaging'),
  });

  useEffect(() => { document.title = `Список сборки · ${new Date().toLocaleDateString('ru-RU')}`; }, []);

  // Аггрегация по offer_id
  const aggregated = useMemo(() => {
    if (!postings.data) return [];
    const map = new Map<string, {
      offer_id: string;
      name: string;
      product_id: number;
      total_qty: number;
      mandatory_mark_required: boolean;
      postings: { posting_number: string; quantity: number }[];
    }>();
    for (const p of postings.data.items) {
      for (const pr of p.products) {
        const key = pr.offer_id;
        const entry = map.get(key);
        if (entry) {
          entry.total_qty += pr.quantity;
          entry.postings.push({ posting_number: p.posting_number, quantity: pr.quantity });
        } else {
          map.set(key, {
            offer_id: pr.offer_id,
            name: pr.name,
            product_id: pr.product_id,
            total_qty: pr.quantity,
            mandatory_mark_required: pr.mandatory_mark_required,
            postings: [{ posting_number: p.posting_number, quantity: pr.quantity }],
          });
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [postings.data]);

  if (postings.isLoading) return <div className="card">Загрузка…</div>;
  if (!postings.data) return <div className="card">Ошибка загрузки</div>;

  const totalItems = aggregated.reduce((s, a) => s + a.total_qty, 0);
  const today = new Date();

  return (
    <div className="picking-list">
      {/* Управление — скрыто при печати */}
      <div className="page-header no-print">
        <div>
          <Link to="/shipments" className="btn" style={{ marginBottom: 8 }}>
            <IoArrowBackOutline /> К отгрузкам
          </Link>
          <h2 style={{ marginTop: 4 }}>Список сборки</h2>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Печатная форма для склада — все отправления в статусе «Ожидают сборки».
          </div>
        </div>
        <button className="btn primary" onClick={() => window.print()} style={{ padding: '10px 20px', fontSize: 14 }}>
          <IoPrintOutline /> Распечатать
        </button>
      </div>

      {/* Выбор режима — скрыт при печати */}
      <div className="status-tabs no-print" style={{ maxWidth: 600 }}>
        <button className={`status-tab${mode === 'aggregated' ? ' active' : ''}`} onClick={() => setMode('aggregated')}>
          К сбору (по артикулу)
        </button>
        <button className={`status-tab${mode === 'detailed' ? ' active' : ''}`} onClick={() => setMode('detailed')}>
          Детализация по отправлениям
        </button>
      </div>

      {/* Печатная шапка */}
      <div className="picking-list-header">
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Лист сборки FBS</h1>
          <div className="picking-list-subtitle">
            Магазин: <strong>koleso.app</strong> · Ozon · {today.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} {today.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
        <div className="picking-list-summary">
          <div className="picking-list-stat">
            <div className="picking-list-stat-num">{postings.data.total}</div>
            <div className="picking-list-stat-label">отправлений</div>
          </div>
          <div className="picking-list-stat">
            <div className="picking-list-stat-num">{aggregated.length}</div>
            <div className="picking-list-stat-label">артикулов</div>
          </div>
          <div className="picking-list-stat">
            <div className="picking-list-stat-num">{totalItems}</div>
            <div className="picking-list-stat-label">единиц</div>
          </div>
        </div>
      </div>

      {mode === 'aggregated' && (
        <>
          <h3 className="picking-list-section-title">К сбору (по артикулу)</h3>
          <table className="picking-table">
            <thead>
              <tr>
                <th style={{ width: 30 }}>✓</th>
                <th style={{ width: 80 }}>Артикул</th>
                <th>Наименование</th>
                <th style={{ width: 80, textAlign: 'right' }}>Кол-во</th>
                <th style={{ width: 100 }}>Маркировка</th>
                <th>Отправления</th>
              </tr>
            </thead>
            <tbody>
              {aggregated.map((a) => (
                <tr key={a.offer_id}>
                  <td><div className="picking-checkbox" /></td>
                  <td><code style={{ fontSize: 12 }}>{a.offer_id}</code></td>
                  <td>{a.name}</td>
                  <td style={{ textAlign: 'right' }}><strong style={{ fontSize: 16 }}>{a.total_qty}</strong></td>
                  <td>{a.mandatory_mark_required ? <strong>«Честный знак»</strong> : <span className="muted">—</span>}</td>
                  <td style={{ fontSize: 11 }}>
                    {a.postings.map((p) => `${p.posting_number} (${p.quantity})`).join(', ')}
                  </td>
                </tr>
              ))}
              {aggregated.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24 }} className="muted">
                  Нет отправлений в статусе «Ожидают сборки».
                </td></tr>
              )}
            </tbody>
          </table>
        </>
      )}

      {mode === 'detailed' && (
        <>
          <h3 className="picking-list-section-title">Детализация по отправлениям</h3>
          {postings.data.items.map((p) => (
            <div key={p.posting_number} className="picking-posting">
              <div className="picking-posting-header">
                <div>
                  <strong style={{ fontSize: 15 }}>{p.posting_number}</strong>
                  {p.delivery_method_name && <span className="muted" style={{ marginLeft: 12, fontSize: 12 }}>{p.delivery_method_name}</span>}
                </div>
                {p.shipment_date && (
                  <span className="muted" style={{ fontSize: 12 }}>
                    до {new Date(p.shipment_date).toLocaleString('ru-RU')}
                  </span>
                )}
              </div>
              <table className="picking-table picking-table-inner">
                <thead>
                  <tr>
                    <th style={{ width: 30 }}>✓</th>
                    <th style={{ width: 80 }}>Артикул</th>
                    <th>Наименование</th>
                    <th style={{ width: 70, textAlign: 'right' }}>Кол-во</th>
                    <th style={{ width: 100 }}>Маркировка</th>
                  </tr>
                </thead>
                <tbody>
                  {p.products.map((pr) => (
                    <tr key={pr.product_id}>
                      <td><div className="picking-checkbox" /></td>
                      <td><code style={{ fontSize: 11 }}>{pr.offer_id}</code></td>
                      <td>{pr.name}</td>
                      <td style={{ textAlign: 'right' }}><strong>{pr.quantity}</strong></td>
                      <td>{pr.mandatory_mark_required ? 'Честный знак' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {postings.data.items.length === 0 && (
            <div className="muted" style={{ textAlign: 'center', padding: 24 }}>Нет отправлений.</div>
          )}
        </>
      )}

      <div className="picking-list-footer">
        <div>
          Собрал: __________________________________ ___________ (подпись)
        </div>
        <div style={{ marginTop: 16 }}>
          Дата сборки: ________________  Время: ________________
        </div>
      </div>
    </div>
  );
}
